<template>
  <div :class="rootClasses" :style="rootStyles">
    <ElementControls
      v-if="editing"
      :no-page-break="!pageBreakAllowed"
      @delete="$emit('delete')"
      @duplicate="$emit('duplicate')"
      @page-break="$emit('page-break')"
      @hide="$emit('hide')"
    />
    <AnalyticsWrapper>
      <div :class="layoutClasses">
        <div
          v-if="label || description"
          class="formester-label-description-wrapper"
          :style="labelDescriptionWrapperStyles"
        >
          <label
            :for="item.label + item.id"
            :class="labelClasses"
            :style="labelStyles"
            v-html="label"
          />

          <SmartText
            :active="active"
            :editing="editing"
            :item="item"
            :style="descriptionStyles"
            :text="description"
          />
        </div>

        <div class="d-flex align-items-center">
          <div class="phone-field-container">
            <input
              :id="item.label + item.id"
              ref="phoneNumber"
              v-model="displayValue"
              v-maska:[phoneMask]
              :data-uuid="item.id"
              class="phone-number-input"
              :class="inputClasses"
              :readonly="editing"
              :style="inputStyles"
              :placeholder="placeholder"
              :name="item.label"
              :required="item.required"
              :type="item.validate"
              :disabled="isDisabled"
              @focus="onInputFocus"
              @blur="onInputBlur"
            />

            <ElPopover
              v-if="item.showCountrySelector"
              ref="countryPopover"
              placement="bottom-end"
              width="auto"
              trigger="click"
              :offset="6"
              :show-arrow="false"
              :popper-style="popperStyle"
            >
              <template #reference>
                <div
                  class="selected-option"
                  :style="selectedOption"
                  @click.stop="openCountrySelector"
                >
                  <img
                    :src="imageUrl"
                    :alt="item.selectedCountry.name"
                    :style="`opacity: ${countryCode ? 1 : 0}`"
                  />
                  <i class="fa fa-chevron-down" aria-hidden="true" />
                </div>
              </template>
              <Suspense v-if="showCountrySelector">
                <template #default>
                  <CountrySelector
                    :selected-country="item.selectedCountry"
                    :primary-color="styling.buttonColor"
                    @select="updateSelectedCountry"
                    @close="closeCountrySelector"
                  />
                </template>
                <template #fallback>
                  <div class="spin-wrapper">
                    <PgLoader loading :color="styling.buttonColor" />
                  </div>
                </template>
              </Suspense>
            </ElPopover>
          </div>
        </div>
        <ErrorMessage :item="item" />
      </div>
    </AnalyticsWrapper>
  </div>
</template>

<script>
import { vMaska } from "maska/vue";
import { ElPopover } from "element-plus";

import ElementControls from "../element-controls.vue";
import commonStyles from "../mixins/commonStyles";
import sharedActions from "../mixins/sharedActions";
import { defineAsyncComponent } from "vue";
import PgLoader from "@/components/shared/loading.vue";
import SmartText from "../shared/smart-text.vue";
import setItem from "lodash/set";
import AnalyticsWrapper from "../analytics-wrapper.vue";
import ErrorMessage from "@/components/builder/components/error-message.vue";
import countryCodes from "@/components/builder/components/phone/country-codes";

export default {
  name: "Phone",
  directives: { maska: vMaska },
  components: {
    ElementControls,
    CountrySelector: defineAsyncComponent(
      () => import("./country-selector.vue"),
    ),
    PgLoader,
    SmartText,
    ElPopover,
    AnalyticsWrapper,
    ErrorMessage,
  },
  mixins: [commonStyles, sharedActions],
  props: {
    item: {
      type: Object,
      required: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    editing: {
      type: Boolean,
      default: false,
    },
    active: {
      type: Boolean,
      default: false,
    },
    styling: {
      type: Object,
      default: () => {
        return {};
      },
    },
    pageBreakAllowed: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["delete", "page-break", "duplicate", "hide"],
  data() {
    return {
      showCountrySelector: false,
      isFocused: false,
    };
  },
  computed: {
    displayValue: {
      get() {
        const code = this.countryDialCode?.slice(1);

        if (this.editing) {
          return "";
        }

        if (!this.item.showCountrySelector) {
          return this.itemValue;
        }

        if (!this.itemValue && this.isFocused) {
          return `+${code}`;
        }

        if (this.itemValue) {
          return code
            ? `+${code} ${this.formatPhoneNumber(this.itemValue)}`
            : `+${this.itemValue}`;
        }

        return "";
      },
      set(newValue) {
        if (this.editing) {
          return;
        }

        if (!newValue || newValue === "+") {
          setItem(this.item, "value", "");
          setItem(this.item, "selectedCountry", {});
          return;
        }

        if (!this.item.showCountrySelector) {
          this.itemValue = newValue.replace(/\s+/g, "");
          return;
        }

        // If country selector is enabled, find the country and format the phone number
        let value = newValue.replace(/\s+/g, "");
        const matchingCountries = countryCodes.filter(
          (country) =>
            country.dialCode &&
            (value.startsWith(country.dialCode) ||
              value.startsWith(country.dialCode.slice(1))),
        );

        // Sort matching countries by dial code length to get the longest match
        matchingCountries.sort((a, b) => b.dialCode.length - a.dialCode.length);
        let matchedCountry = matchingCountries[0];

        if (matchedCountry) {
          value = value.replace(matchedCountry.dialCode, "");

          // If the dial code is +1, set the country to US (default) instead of Canada
          if (matchedCountry.dialCode === "+1") {
            matchedCountry = this.usCountry;
          }

          if (matchedCountry.dialCode !== this.countryDialCode) {
            setItem(this.item, "selectedCountry", matchedCountry);
          }
        } else {
          setItem(this.item, "selectedCountry", {});
        }

        this.itemValue = value.replace(/[-\s+]/g, "");
      },
    },
    inputClasses() {
      return {
        "formester-input": true,
        "formester-input__shorttext": true,
      };
    },
    selectedOption() {
      return {
        color: this.styling.fontColor || "#575757",
      };
    },
    countryCode() {
      return this.item.selectedCountry?.code;
    },
    countryDialCode() {
      return this.item.selectedCountry?.dialCode || "";
    },
    imageUrl() {
      return new URL(`./flags/${this.countryCode}.png`, import.meta.url).href;
    },
    popperStyle() {
      return {
        padding: 0,
      };
    },
    phoneMasksByCountry() {
      return countryCodes.reduce(
        (phoneMasks, country) => ({
          ...phoneMasks,
          [country.code]: country.phoneNumberMask,
        }),
        {},
      );
    },
    usCountry() {
      return countryCodes.find((country) => country.code === "us");
    },
    phoneMask() {
      const countryPhoneMask = this.phoneMasksByCountry[this.countryCode];

      return {
        countryPhoneMask,
        mask: (value) => {
          if (value.length <= countryPhoneMask.length) {
            return countryPhoneMask;
          } else {
            const extraDigits = value.length - countryPhoneMask.length;
            return `${countryPhoneMask} ${"#".repeat(extraDigits)}`;
          }
        },
      };
    },
    placeholder() {
      return this.item.smartPlaceholder
        ? `${this.countryDialCode} ${this.replaceWithRandomDigits(this.phoneMask.countryPhoneMask)}`
        : this.item.placeholder;
    },
  },
  mounted() {
    if (this.itemValue) {
      this.displayValue = this.itemValue;
    }
  },
  methods: {
    openCountrySelector() {
      if (this.editing) {
        return;
      }

      this.showCountrySelector = true;
    },
    closeCountrySelector() {
      this.$refs.countryPopover.hide();
    },
    updateSelectedCountry(country) {
      setItem(this.item, "selectedCountry", country);
    },
    replaceWithRandomDigits(inputString) {
      return inputString?.replace(/#/g, () => Math.floor(Math.random() * 10));
    },
    formatPhoneNumber(value) {
      if (!value) return "";

      const mask = this.phoneMasksByCountry[this.countryCode] || "";
      const digits = value.replace(/\D/g, "");
      let result = "";
      let digitIndex = 0;

      // Apply the formatting based on the mask pattern
      for (let i = 0; i < mask.length && digitIndex < digits.length; i++) {
        if (mask[i] === "#") {
          result += digits[digitIndex];
          digitIndex++;
        } else {
          result += mask[i];
          // Only add the separator if we have more digits to add
          if (digitIndex < digits.length) {
            result += "";
          }
        }
      }

      return result;
    },
    onInputFocus() {
      this.isFocused = true;
    },
    onInputBlur() {
      this.isFocused = false;
    },
  },
};
</script>

<style scoped>
.phone-field-container {
  position: relative;
  width: 100%;
}
.selected-option {
  box-sizing: border-box;
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  padding: 12px;
  width: 65px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}
.selected-option:hover {
  scale: 1.01;
}
.selected-option img {
  width: 22px;
  border-radius: 50%;
}
.selected-option .fa-chevron-down {
  color: var(--answer-input-color);
  font-size: 12px;
}
.phone-number-input {
  padding-right: 75px;
}
.spin-wrapper {
  height: 300px;
  width: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media screen and (max-width: 768px) {
  .spin-wrapper {
    height: 320px;
    width: 260px;
  }
}
</style>
